import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, map, mergeMap} from 'rxjs/operators';
import {FollowApiModel, SubjectApiModel} from '../../../../../api/models/follow.api-model';
import {UserApiModel} from '../../../../../api/models/user.api-model';
import {MediaObjectViewModel} from '../../../../../core/models/media-object.view-model';
import {
  FollowedSubjectInterface,
  FollowerListItemInterface
} from '../../../../../core/interfaces/follower-item.interface';
import {SocialListHydrationService} from '../social-list-hydration.service';

@Injectable({providedIn: 'root'})
export class FollowListItemsBuilderService {
  constructor(
    private readonly hydrationService: SocialListHydrationService,
  ) {
  }

  public buildItems$(followAMs: FollowApiModel[], fallbackSubject?: SubjectApiModel): Observable<FollowerListItemInterface[]> {
    if (!followAMs?.length) {
      return of([]);
    }

    const followerRefs = followAMs.map((followAM) => followAM.followedBy);

    return this.hydrationService.getUsersByReferences$(followerRefs).pipe(
      mergeMap((usersMap) => {
        const groupedFollowerItems = followAMs.reduce((grouped, followAM) => {
          const followerId = this.hydrationService.extractResourceIri(followAM.followedBy);
          const follower = followerId ? usersMap.get(followerId) : null;
          if (!follower?.['@id'] || follower.deleted) {
            return grouped;
          }

          const followed = (typeof followAM.followed === 'string' ? fallbackSubject : followAM.followed) as SubjectApiModel;
          if (!followed?.['@id']) {
            return grouped;
          }

          const followedSubject: FollowedSubjectInterface = {
            '@id': followed['@id'],
            apiModel: followed,
            avatar: null,
            slug: followed.slug,
            identity: followed.identity
          };

          const existing = grouped.find((item) => item.follower['@id'] === follower['@id']);
          if (existing) {
            existing.ids.push(followAM['@id']);
            existing.followed.push(followedSubject);
          } else {
            grouped.push({
              ids: [followAM['@id']],
              follower: {
                '@id': follower['@id'],
                apiModel: follower,
                avatar: null,
                slug: follower.slug,
                username: follower.username
              },
              followed: [followedSubject],
              resource: 'follows'
            });
          }

          return grouped;
        }, [] as FollowerListItemInterface[]);

        const mediaRefs = [
          ...Array.from(usersMap.values()).map((userAM: UserApiModel) => userAM.avatar),
          ...groupedFollowerItems.flatMap((item) => item.followed.map((followed) => followed.apiModel.avatar))
        ];

        return this.hydrationService.getMediaMap$(mediaRefs).pipe(
          map((mediaMap) => groupedFollowerItems.map((item) => this.hydrateFollowerItem(item, mediaMap)))
        );
      }),
      catchError(() => of([]))
    );
  }

  public mergeItems(existingItems: FollowerListItemInterface[], incomingItems: FollowerListItemInterface[]): FollowerListItemInterface[] {
    const mergedByFollower = new Map<string, FollowerListItemInterface>();

    [...existingItems, ...incomingItems].forEach((item) => {
      const followerId = item?.follower?.['@id'];
      if (!followerId) {
        return;
      }

      const existing = mergedByFollower.get(followerId);
      if (!existing) {
        mergedByFollower.set(followerId, {
          ...item,
          ids: [...item.ids],
          followed: [...item.followed]
        });
        return;
      }

      const existingIds = new Set(existing.ids);
      item.ids.forEach((id, index) => {
        if (existingIds.has(id)) {
          return;
        }

        existing.ids.push(id);
        existing.followed.push(item.followed[index]);
        existingIds.add(id);
      });
    });

    return Array.from(mergedByFollower.values());
  }

  private hydrateFollowerItem(
    item: FollowerListItemInterface,
    mediaMap: Map<string, MediaObjectViewModel>
  ): FollowerListItemInterface {
    const followerAvatarId = this.hydrationService.extractResourceIri(item.follower.apiModel.avatar);
    item.follower.avatar = followerAvatarId ? mediaMap.get(followerAvatarId) || null : null;

    item.followed.forEach((followed) => {
      const followedAvatarId = this.hydrationService.extractResourceIri(followed.apiModel.avatar);
      followed.avatar = followedAvatarId ? mediaMap.get(followedAvatarId) || null : null;
    });

    return item;
  }
}
